import theme from "@/styles/theme";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { NoDataFound } from "@/components/NoDataFound";
import { useDevelopmentPrograms } from "@/lib/api";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { grey } from "@mui/material/colors";
import { ContentContainer } from "@/components/ContentContainer";
import { DevelopmentProgramEntry } from "@/components/foerderung/DevelopmentProgramEntry";
import { CollapseImageLeftTextRight } from "@/components/CollapseImageLeftTextRight";
import { Box, Fade } from "@mui/material";
import React, { useEffect, useState } from "react";
import Error from "next/error";
import PageHead from "@/components/PageHead";
import { FoerderfinderFilterContainer } from "@/components/foerderung/FoerderfinderFilterContainer";
import { SkeletonStyled } from "@/components/SkeletonStyled";
import { CircularProgressStyled } from "@/components/CircularProgressStyled";

export default function DevelopmentProgramIndex({ page, ref, ...props }) {
  const [filterConfig, setFilterConfig] = useState({});
  const { developmentPrograms, isLoading, isValidating, isError, mutate } =
    useDevelopmentPrograms(filterConfig);
  const [programsToDisplay, setProgramsToDisplay] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    setProgramsToDisplay(developmentPrograms);
  }, [developmentPrograms]);

  const handleFilterChanged = (event) => {
    const re = new RegExp(event.target.value, "gi");
    const filteredPrograms = developmentPrograms.filter((dp) => {
      return dp.title.match(re);
    });
    setProgramsToDisplay(filteredPrograms);
  };

  const setSort = (event) => {
    filterConfig._sort = event.target.value;

    mutate();
  };

  const handleInputChanged = (event) => {
    const newConfig = { ...filterConfig };
    if (!event.target.value) {
      delete newConfig[event.target.name];
    }

    newConfig[event.target.name] = event.target.value?.id ?? event.target.value;

    setFilterConfig(newConfig);

    mutate();
  };

  if (isError) {
    return <Error statusCode={404} />;
  }

  return (
    <>
      <PageHead page={page} />

      <ContentContainer
        headline={page.headline}
        image={page?.hero_image?.formats?.large?.url}
      >
        <Grid container>
          <Grid item xs={12}>
            <Grid container spacing={{ xs: 1, sm: 3 }}>
              <Grid
                item
                xs={12}
                sx={{
                  display: { xs: "block", sm: "none" },
                }}
              >
                {filterOpen ? (
                  <Fade in={filterOpen}>
                    <Box ref={ref} {...props}>
                      <FoerderfinderFilterContainer
                        filterMobile={true}
                        onCancel={() => setFilterOpen(false)}
                        handleInputChanged={handleInputChanged}
                        handleFilterChanged={handleFilterChanged}
                        filterOpen
                      />
                    </Box>
                  </Fade>
                ) : (
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    startIcon={<FilterAltIcon />}
                    onClick={() => setFilterOpen(true)}
                  >
                    Filter anzeigen
                  </Button>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  display: { xs: "none", sm: "block" },
                  mb: 4,
                  "& .MuiAutocomplete-root:not(:last-of-type)": {
                    mb: 1,
                  },
                }}
              >
                <Typography variant="h4" sx={{ mb: 1 }}>
                  Filter
                </Typography>

                <FoerderfinderFilterContainer
                  handleInputChanged={handleInputChanged}
                  handleFilterChanged={handleFilterChanged}
                  setSort={setSort}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={1}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      [theme.breakpoints.up("sm")]: { mb: "-11px" },
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        display: { xs: "none", sm: "block" },
                        mb: 1,
                      }}
                    >
                      Förderprogramme
                    </Typography>
                    <Grid
                      container
                      alignItems="center"
                      sx={{
                        [theme.breakpoints.up("sm")]: {
                          mt: "-.625rem",
                        },
                      }}
                    >
                      <Grid item xs={8} sx={{ mb: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            textTransform: "uppercase",
                            color: grey[600],
                          }}
                        >
                          <>
                            {isValidating ? (
                              <>
                                <CircularProgressStyled /> Förderungen gefunden
                              </>
                            ) : (
                              <>
                                {programsToDisplay?.length} Förderungen gefunden
                              </>
                            )}
                          </>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    {isLoading ? (
                      <SkeletonStyled type="rectangle" />
                    ) : (
                      <>
                        {(!developmentPrograms ||
                          developmentPrograms.length === 0) && (
                          <NoDataFound type="Förderprogramme" />
                        )}
                        {programsToDisplay?.map((program) => (
                          <CollapseImageLeftTextRight key={program.id}>
                            <DevelopmentProgramEntry program={program} />
                          </CollapseImageLeftTextRight>
                        ))}
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </ContentContainer>
    </>
  );
}

export const getStaticProps = async () => {
  const pageResponse = await fetch(
    `${
      process.env.NEXT_PUBLIC_API_URL || "http://localhost:1337"
    }/developmentprograms`,
  );
  const page = await pageResponse.json();

  return {
    props: {
      page,
    },
    revalidate: parseInt(process.env.SSG_REVALIDATE_TIMEOUT),
  };
};
